import type { PriceCardContentKey } from './rateCardPlatformConfig';

export interface SuggestedRateRange {
    min: number;
    max: number;
    midpoint: number;
}

const usd = (n: number) => Math.max(0, Math.round(n));

/**
 * Tiered UGC rate suggestions from follower count (USD).
 * Ranges are calibrated so ~50k Instagram Reel lands near $450–$650.
 */
export function suggestRateRange(followers: number, contentKey: PriceCardContentKey): SuggestedRateRange {
    const f = Math.max(0, followers);
    const tier = followerTier(f);

    const multipliers: Record<PriceCardContentKey, { min: number; max: number }> = {
        reel: { min: 0.007, max: 0.013 },
        story: { min: 0.003, max: 0.006 },
        post: { min: 0.005, max: 0.009 },
        video: { min: 0.008, max: 0.014 },
        igtv: { min: 0.01, max: 0.016 },
        bundle: { min: 0.02, max: 0.035 },
        'live stream': { min: 0.012, max: 0.02 },
        'sponsored post': { min: 0.006, max: 0.011 },
        'product review': { min: 0.007, max: 0.012 },
        'brand mention': { min: 0.004, max: 0.008 },
        'event coverage': { min: 0.015, max: 0.025 },
    };

    const m = multipliers[contentKey] ?? { min: 0.006, max: 0.01 };
    const floor = tierFloor(tier, contentKey);
    const min = usd(Math.max(floor.min, f * m.min * tierScale(tier, 'min')));
    const max = usd(Math.max(min + 25, f * m.max * tierScale(tier, 'max')));
    const midpoint = usd((min + max) / 2);
    return { min, max, midpoint };
}

function followerTier(followers: number): 'nano' | 'micro' | 'mid' | 'macro' | 'mega' {
    if (followers < 1_000) return 'nano';
    if (followers < 10_000) return 'micro';
    if (followers < 100_000) return 'mid';
    if (followers < 500_000) return 'macro';
    return 'mega';
}

function tierScale(tier: ReturnType<typeof followerTier>, bound: 'min' | 'max'): number {
    const table = {
        nano: { min: 1.4, max: 1.5 },
        micro: { min: 1.15, max: 1.2 },
        mid: { min: 1, max: 1 },
        macro: { min: 0.92, max: 0.95 },
        mega: { min: 0.85, max: 0.88 },
    };
    return table[tier][bound];
}

function tierFloor(tier: ReturnType<typeof followerTier>, contentKey: PriceCardContentKey): { min: number } {
    const isStory = contentKey === 'story' || contentKey === 'brand mention';
    const isVideo = contentKey === 'video' || contentKey === 'reel' || contentKey === 'live stream';
    if (tier === 'nano') {
        return { min: isStory ? 15 : isVideo ? 35 : 25 };
    }
    if (tier === 'micro') {
        return { min: isStory ? 40 : isVideo ? 75 : 60 };
    }
    return { min: isStory ? 75 : isVideo ? 120 : 100 };
}

export function formatUsdRange(min: number, max: number): string {
    const fmt = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    });
    return `${fmt.format(min)} - ${fmt.format(max)}`;
}

export function formatFollowersForInsight(followers: number): string {
    if (followers >= 1_000_000) return `${(followers / 1_000_000).toFixed(1)}M`;
    if (followers >= 1_000) return `${Math.round(followers / 1_000)}k`;
    return String(Math.round(followers));
}
